import React, { useEffect, useState } from "react";
import { Input, Select, DatePicker } from "antd";
import { Search } from "lucide-react";
import dayjs from "dayjs";

export interface SearchComponentProps {
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;

  // Status Filter
  showStatusFilter?: boolean;
  statusValue?: string;
  onStatusChange?: (value: string) => void;
  // Override the default Active/Inactive options - e.g. Products adds an
  // "Awaiting Approval" value distinct from plain Active/Inactive.
  statusOptions?: { label: string; value: string }[];

  // Date Range Filter (using dayjs)
  showDateRangeFilter?: boolean;
  dateRangeValue?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
  onDateRangeChange?: (
    dates: any,
    dateStrings: [string, string],
  ) => void;

  // Additional generic filters can be passed as children
  children?: React.ReactNode;
}

const STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

export const SearchComponent: React.FC<SearchComponentProps> = ({
  searchValue,
  onSearchChange,
  placeholder = "Search...",
  debounceMs = 500,
  showStatusFilter = true,
  statusValue = "",
  onStatusChange,
  statusOptions,
  showDateRangeFilter = false,
  dateRangeValue = null,
  onDateRangeChange,
  children,
}) => {
  const [localSearch, setLocalSearch] = useState(searchValue);

  // Sync prop -> local if it changes from outside
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  const onSearchChangeRef = React.useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  const isInitialMount = React.useRef(true);

  // Debounce logic - only run when user actually types in localSearch
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const handler = setTimeout(() => {
      onSearchChangeRef.current(localSearch);
    }, debounceMs);

    return () => clearTimeout(handler);
  }, [localSearch, debounceMs]);

  return (
    <div className="flex flex-wrap gap-3 mb-4 w-full">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-2 text-gray-400" size={16} />
        <Input
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder={placeholder}
          className="h-[35px]"
          allowClear
        />
      </div>

      {showStatusFilter && onStatusChange && (
        <Select
          value={statusValue}
          onChange={onStatusChange}
          options={statusOptions || STATUS_OPTIONS}
          className="w-40 h-[35px]"
          placeholder="Filter by status"
        />
      )}

      {showDateRangeFilter && onDateRangeChange && (
        <DatePicker.RangePicker
          value={dateRangeValue as any}
          onChange={onDateRangeChange as any}
          className="h-[35px]"
          format="YYYY-MM-DD"
          allowClear
          placeholder={["Start Date", "End Date"]}
        />
      )}

      {/* Render any additional filters passed via children */}
      {children}
    </div>
  );
};

export default SearchComponent;
